import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from "@mui/material";

const dayOptions = [
  { value: "MONDAY", label: "Lunedì" },
  { value: "TUESDAY", label: "Martedì" },
  { value: "WEDNESDAY", label: "Mercoledì" },
  { value: "THURSDAY", label: "Giovedì" },
  { value: "FRIDAY", label: "Venerdì" },
  { value: "SATURDAY", label: "Sabato" },
  { value: "SUNDAY", label: "Domenica" },
];

const defaultForm = {
  dipendenteId: "",
  giorniSettimana: ["MONDAY"],
  tipoDisponibilita: "DISPONIBILITA",
  pranzo: true,
  cena: false,
  localeIds: [],
};

export default function DisponibilitaModal({ open, onClose, onSubmit, availability, employees, locali = [], mode = "create" }) {
  const [form, setForm] = useState(defaultForm);
  const isEditMode = mode === "edit";
  const isDuplicateMode = mode === "duplicate";

  useEffect(() => {
    if (!availability) {
      setForm(defaultForm);
      return;
    }

    setForm({
      dipendenteId: availability?.dipendente?.id || "",
      giorniSettimana: availability.giorniSettimana || [availability.giornoSettimana || availability.dayOfWeek || "MONDAY"],
      tipoDisponibilita: availability?.tipoDisponibilita || "DISPONIBILITA",
      pranzo: Boolean(availability.pranzo),
      cena: Boolean(availability.cena),
      localeIds: (availability.localeIds || availability.locali || []).map((item) =>
        typeof item === "object" ? Number(item.id) : Number(item)
      ),
    });
  }, [availability]);

  const isValid = useMemo(() => {
    const hasValidServiceSelection =
      form.tipoDisponibilita === "REPERIBILITA" ? true : form.pranzo || form.cena;

    return (
      !!form.dipendenteId &&
      hasValidServiceSelection &&
      form.localeIds.length > 0 &&
      form.giorniSettimana.length > 0
    );
  }, [form]);

  const updateField = (field) => (event) => {
    const value = event.target.value;

    if (field === "tipoDisponibilita") {
      setForm((prev) => ({
        ...prev,
        tipoDisponibilita: value,
        pranzo: value === "REPERIBILITA" ? false : prev.pranzo,
        cena: value === "REPERIBILITA" ? false : prev.cena,
      }));
      return;
    }

    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateLocaleIds = (event) => {
    const { value } = event.target;
    const rawValues = typeof value === "string" ? value.split(",") : value;
    const nextLocaleIds = (rawValues || [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));

    setForm((prev) => ({ ...prev, localeIds: nextLocaleIds }));
  };

  const updateBoolean = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.checked }));
  };

  const updateGiorniSettimana = (event) => {
    const { value } = event.target;
    const rawValues = typeof value === "string" ? value.split(",") : value;
    const nextDays = (rawValues || []).filter(Boolean);

    setForm((prev) => ({ ...prev, giorniSettimana: nextDays }));
  };

  const handleSubmit = () => {
    if (!isValid) {
      return;
    }

    onSubmit({
      ...form,
      dipendenteId: Number(form.dipendenteId),
      giorniSettimana: form.giorniSettimana,
      pranzo: form.tipoDisponibilita === "REPERIBILITA" ? false : form.pranzo,
      cena: form.tipoDisponibilita === "REPERIBILITA" ? false : form.cena,
      localeIds: form.localeIds.map((id) => Number(id)),
    });
  };

  const localiLabel = `Locali abilitati (${form.localeIds.length})`;
  const giorniLabel = `Giorni della settimana (${form.giorniSettimana.length})`;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isEditMode ? "Modifica disponibilità" : isDuplicateMode ? "Duplica disponibilità" : "Nuova disponibilità"}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Dipendente"
              value={form.dipendenteId}
              onChange={updateField("dipendenteId")}
              disabled={isEditMode}
              required
            >
              {employees.map((employee) => (
                <MenuItem key={employee.id} value={employee.id}>
                  {employee.nome}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Tipo disponibilità"
              value={form.tipoDisponibilita}
              onChange={updateField("tipoDisponibilita")}
              required
            >
              <MenuItem value="DISPONIBILITA">Disponibilità</MenuItem>
              <MenuItem value="REPERIBILITA">Reperibilità</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel id="giorni-settimana-label">{giorniLabel}</InputLabel>
              <Select
                labelId="giorni-settimana-label"
                multiple
                value={form.giorniSettimana}
                onChange={updateGiorniSettimana}
                input={<OutlinedInput label={giorniLabel} />}
                renderValue={(selected) => {
                  const selectedArray = Array.isArray(selected) ? selected : [selected];
                  return dayOptions
                    .filter((option) => selectedArray.includes(option.value))
                    .map((option) => option.label)
                    .join(", ");
                }}
              >
                {dayOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox checked={form.giorniSettimana.includes(option.value)} size="small" />
                    <ListItemText primary={option.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.pranzo}
                  onChange={updateBoolean("pranzo")}
                  disabled={form.tipoDisponibilita === "REPERIBILITA"}
                />
              }
              label="Disponibile a pranzo"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.cena}
                  onChange={updateBoolean("cena")}
                  disabled={form.tipoDisponibilita === "REPERIBILITA"}
                />
              }
              label="Disponibile a cena"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel id="locali-abilitati-label">{localiLabel}</InputLabel>
              <Select
                labelId="locali-abilitati-label"
                multiple
                value={form.localeIds}
                onChange={updateLocaleIds}
                input={<OutlinedInput label={localiLabel} />}
                renderValue={(selected) => {
                  const selectedArray = Array.isArray(selected) ? selected : [selected];
                  const selectedIds = selectedArray
                    .map((id) => Number(id))
                    .filter((id) => Number.isFinite(id));

                  return locali
                    .filter((locale) => selectedIds.includes(Number(locale.id)))
                    .map((locale) => locale.nome)
                    .join(", ");
                }}
              >
                {locali.map((locale) => (
                  <MenuItem key={locale.id} value={Number(locale.id)}>
                    <Checkbox checked={form.localeIds.includes(Number(locale.id))} size="small" />
                    <ListItemText primary={`${locale.nome} (${locale.codice || "N/A"})`} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annulla</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!isValid}>
          {isEditMode ? "Salva modifiche" : isDuplicateMode ? "Duplica" : "Salva"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
